<script lang="ts" setup>
import { ConnectionStatus, ConnectionType, Network } from "@capacitor/network";
import {
  IonApp,
  IonRouterOutlet,
  modalController,
  toastController,
} from "@ionic/vue";
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useFabriqStore } from "@/store/fabriq";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import storage from "@/utils/storage";
import { SplashScreen } from "@capacitor/splash-screen";
import { Keyboard } from "@capacitor/keyboard";
import { localLogger } from "./utils/localLogger";
import { performManualUpdate, reload } from "@/utils/live-updates";
import { getCurrentBuild } from "@/utils/getCurrentBuild";
import { lockOrientation } from "./utils/lockOrientation";

import { App as CapacitorApp } from "@capacitor/app";
import { PushNotificationsClient } from "@/services/clients";
import { useI18n } from "./composables/useI18n";
import { AuthenticationFailedException } from "./classes/errors";
import AuthenticationFailedExceptionDisplay from "@/components/errors/auth/AuthenticationFailedExceptionDisplay.vue";
import GenericAppInitializationErrorDisplay from "@/components/errors/auth/GenericAppInitializationErrorDisplay.vue";
import DiagnosticToolModal from "@/components/modals/DiagnosticToolModal.vue";
import { calcModalPercents } from "@/utils/modals";
import loader from "@/utils/loader";

import {
  buildGestureHandler,
  buildGestureSequenceHandler,
} from "./utils/gestureHandling";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const fabriqStore = useFabriqStore();
const { connected, initializationState, token, platform } =
  storeToRefs(fabriqStore);

const connections: Array<ConnectionType> = ["wifi", "cellular"];
const onlineListener = async (status: ConnectionStatus) => {
  fabriqStore.setOnline(
    status.connected && connections.includes(status.connectionType)
  );
  await localLogger.log(`Network status changed: ${status.connected}`);
};
Network.addListener("networkStatusChange", onlineListener);

async function getBuildVersion() {
  try {
    fabriqStore.setBuildVersionFromStorage();
    const buildVersion = await getCurrentBuild();
    fabriqStore.setBuildVersion(buildVersion);
    performManualUpdate();
  } catch (e) {
    console.error(e);
    fabriqStore.reportSentry(new Error("Failed to get build version"));
  }
}
const resumed = ref(false);

CapacitorApp.addListener("resume", () => {
  resumed.value = true;
});

watch([token, resumed], async ([token, resumed]) => {
  if (!resumed || !token) return;
  const requestUuids = await storage.retrieveDeletedRequests();
  await PushNotificationsClient.delete("/cached-requests", undefined, {
    requestUuids,
  });
});

onMounted(async () => {
  let shouldBackButtonMinimizeTheApp = false;

  window.addEventListener("touchstart", onTouchStart, { passive: true });
  window.addEventListener("touchend", onTouchEnd, { passive: true });

  CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
    await localLogger.log(`Application is ${isActive ? "active" : "inactive"}`);
    if (!isActive) await localLogger.truncateLogsToLatest();
    if (isActive) return;
  });

  CapacitorApp.addListener("backButton", async ({ canGoBack }) => {
    if (canGoBack) {
      shouldBackButtonMinimizeTheApp = false;
      return;
    }

    if (shouldBackButtonMinimizeTheApp) {
      shouldBackButtonMinimizeTheApp = false;
      CapacitorApp.minimizeApp();
      return;
    }

    const exitToast = await toastController.create({
      message: t("common.exitAppMessage"),
      duration: 2000,
    });

    exitToast.present();
    shouldBackButtonMinimizeTheApp = true;
    setTimeout(() => {
      shouldBackButtonMinimizeTheApp = false;
    }, 2000);
  });

  setTimeout(() => {
    SplashScreen.hide();
  }, 100);
  getBuildVersion();

  await lockOrientation();
});

onUnmounted(() => {
  window.removeEventListener("touchstart", onTouchStart);
  window.removeEventListener("touchend", onTouchEnd);
});

if (platform.value !== "web") {
  Keyboard.addListener("keyboardDidShow", () => {
    document.body.classList.add("keyboard");
  });
  Keyboard.addListener("keyboardDidHide", () => {
    document.body.classList.remove("keyboard");
  });
}

watch(
  () => connected.value && initializationState.value.type !== "pending",
  (value: boolean) => {
    storage.set("connected", connected.value);
    nextTick(() => {
      if (document.body.classList.contains("loaded")) return;
      document.body.classList.add("loaded");
    });
    if (!connected.value && route.path !== "/login" && route.path !== "/")
      return router.replace({ name: "Login" });
    if (value && route.path.startsWith("/login")) {
      const pageToOpenAfterLogin = fabriqStore.pageToOpenAfterLogin;
      if (pageToOpenAfterLogin) {
        return router.replace(pageToOpenAfterLogin);
      }
      return router.replace({ name: "Tickets" });
    }
  }
);
watch(token, (value) => storage.set("token", value));

const isRecovering = ref(false);

async function recoverFromInitializationFailure() {
  isRecovering.value = true;
  try {
    await fabriqStore.logout();
    await fabriqStore.clearUrl();
    await reload();
    isRecovering.value = false;
  } catch (err) {
    fabriqStore.reportSentry(err as Error);
    throw new Error("failed to recover from initialization failure", {
      cause: err,
    });
  }
}

function isAuthenticationError(
  error: unknown
): error is AuthenticationFailedException {
  return (
    error instanceof AuthenticationFailedException &&
    (error.reason === "invalidCredentials" ||
      error.reason === "failedToFinalize")
  );
}

const isDiagnosticToolOpen = ref(false);

const { registerGesture } = buildGestureSequenceHandler(
  ["swipe-left", "swipe-right", "long-press"],
  openDiagnosticToolModal
);

async function openDiagnosticToolModal() {
  if (isDiagnosticToolOpen.value) {
    return;
  }
  isDiagnosticToolOpen.value = true;
  const breakpoints = calcModalPercents(44, 1, 210);
  const modal = await modalController.create({
    component: DiagnosticToolModal,
    canDismiss: true,
    breakpoints,
    initialBreakpoint: breakpoints[1],
    mode: "ios",
  });

  // loader has higher priority, preventing the user from interacting with the diagnostic tool modal
  // hidding it allows interaction and full app reset.
  loader.hide();

  await modal.present();
  await modal.onDidDismiss();

  isDiagnosticToolOpen.value = false;
}

// diagnostic tool handler
const { onTouchStart, onTouchEnd } = buildGestureHandler(registerGesture);
</script>
<template>
  <ion-app class="hide-scrollbar">
    <ion-router-outlet
      v-if="initializationState.type !== 'error'"
      :swipe-gesture="false"
      class="main-outlet"
    />
    <ion-content v-else fullscreen class="normal">
      <authentication-failed-exception-display
        v-if="isAuthenticationError(initializationState.error)"
        :error="initializationState.error"
      />
      <generic-app-initialization-error-display
        v-else
        :disabled="isRecovering"
        @click="recoverFromInitializationFailure"
      />
    </ion-content>
  </ion-app>
</template>

<style>
* {
  overscroll-behavior-y: contain;
}
</style>
